/**
 * Version Manager
 * Handles automatic version checking and cache clearing when new versions are deployed
 */

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const LOCAL_VERSION_KEY = 'app_version';
const LAST_CHECK_KEY = 'last_version_check';
const API_CHECK_THROTTLE = 60 * 1000; // Throttle API checks to once per minute

interface VersionInfo {
  version: string;
  buildTime: string;
}

/**
 * Get the current version from the server
 */
export const fetchServerVersion = async (): Promise<VersionInfo | null> => {
  try {
    // Add timestamp to prevent caching
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });

    if (!response.ok) {
      console.warn('[Version Manager] Failed to fetch server version');
      return null;
    }

    const versionInfo: VersionInfo = await response.json();
    console.log('[Version Manager] Server version:', versionInfo.version);
    return versionInfo;
  } catch (error) {
    console.error('[Version Manager] Error fetching server version:', error);
    return null;
  }
};

/**
 * Get the locally stored version
 */
export const getLocalVersion = (): string | null => {
  return localStorage.getItem(LOCAL_VERSION_KEY);
};

/**
 * Store the current version locally
 */
export const setLocalVersion = (version: string): void => {
  localStorage.setItem(LOCAL_VERSION_KEY, version);
  console.log('[Version Manager] Local version set to:', version);
};

/**
 * Clear all browser cookies
 */
export const clearAllCookies = (): void => {
  try {
    console.log('[Version Manager] Clearing all cookies...');
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

      // Clear cookie for current domain
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      // Clear cookie for root domain
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      // Clear cookie for parent domain
      const domainParts = window.location.hostname.split('.');
      if (domainParts.length > 1) {
        const parentDomain = domainParts.slice(-2).join('.');
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${parentDomain}`;
      }
    }
    console.log('[Version Manager] Cookies cleared successfully');
  } catch (error) {
    console.error('[Version Manager] Error clearing cookies:', error);
  }
};

/**
 * Clear all app caches and storage
 */
export const clearAllCaches = async (): Promise<void> => {
  try {
    console.log('[Version Manager] Clearing all caches...');

    // Clear service worker caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => {
          console.log('[Version Manager] Deleting cache:', cacheName);
          return caches.delete(cacheName);
        })
      );
    }

    // Clear localStorage (except user session data)
    const keysToPreserve = [
      'authUser',
      'token',
      'refreshToken',
      'user_language',
      'user_preferences'
    ];

    const allKeys = Object.keys(localStorage);
    allKeys.forEach(key => {
      if (!keysToPreserve.includes(key)) {
        localStorage.removeItem(key);
      }
    });

    // Clear sessionStorage (except important data)
    const sessionKeysToPreserve = ['authUser'];
    const allSessionKeys = Object.keys(sessionStorage);
    allSessionKeys.forEach(key => {
      if (!sessionKeysToPreserve.includes(key)) {
        sessionStorage.removeItem(key);
      }
    });

    // Clear all cookies
    clearAllCookies();

    console.log('[Version Manager] All caches cleared successfully');
  } catch (error) {
    console.error('[Version Manager] Error clearing caches:', error);
  }
};

/**
 * Force reload the page with cache bypass
 */
export const forceReload = (): void => {
  console.log('[Version Manager] Forcing page reload...');
  // Use location.reload(true) equivalent for hard reload
  window.location.href = window.location.href.split('?')[0] + '?v=' + Date.now();
};

/**
 * Check if a new version is available and handle updates
 */
export const checkForUpdates = async (options: { autoReload?: boolean } = {}): Promise<boolean> => {
  const { autoReload = false } = options;

  try {
    const serverVersion = await fetchServerVersion();
    if (!serverVersion) {
      return false;
    }

    const localVersion = getLocalVersion();

    // First time setup
    if (!localVersion) {
      setLocalVersion(serverVersion.version);
      return false;
    }

    // Version mismatch detected!
    if (localVersion !== serverVersion.version) {
      console.log('[Version Manager] Version mismatch detected!');
      console.log('[Version Manager] Local:', localVersion, '→ Server:', serverVersion.version);

      // Clear all caches
      await clearAllCaches();

      // Update local version
      setLocalVersion(serverVersion.version);

      // Unregister service worker to force fresh install
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(reg => reg.unregister()));
        console.log('[Version Manager] Service worker unregistered');
      }

      if (autoReload) {
        // Show notification before reload
        showUpdateNotification();

        // Reload after a brief delay to let user see the message
        setTimeout(() => {
          forceReload();
        }, 2000);
      }

      return true; // Update available
    }

    console.log('[Version Manager] Version up to date:', localVersion);
    return false; // No update needed
  } catch (error) {
    console.error('[Version Manager] Error checking for updates:', error);
    return false;
  }
};

/**
 * Show a notification to the user about the update
 */
const showUpdateNotification = (): void => {
  // Create a toast-style notification
  const notification = document.createElement('div');
  notification.innerHTML = `
    <div style="
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4CAF50;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 12px;
    ">
      <i class="ri-refresh-line" style="font-size: 20px;"></i>
      <div>
        <div style="font-weight: 600; margin-bottom: 4px;">Nova versão disponível!</div>
        <div style="opacity: 0.9; font-size: 12px;">Atualizando o aplicativo...</div>
      </div>
    </div>
  `;
  document.body.appendChild(notification);
};

/**
 * Initialize version checking
 */
export const initVersionCheck = (): void => {
  console.log('[Version Manager] Initializing version check...');

  // Check immediately on init
  checkForUpdates({ autoReload: true });

  // Check periodically
  setInterval(() => {
    checkForUpdates({ autoReload: true });
  }, VERSION_CHECK_INTERVAL);

  // Check when page becomes visible again
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      const lastCheck = localStorage.getItem(LAST_CHECK_KEY);
      const now = Date.now();

      // Only check if last check was more than 1 minute ago
      if (!lastCheck || now - parseInt(lastCheck) > 60 * 1000) {
        localStorage.setItem(LAST_CHECK_KEY, now.toString());
        checkForUpdates({ autoReload: true });
      }
    }
  });
};

/**
 * Check version on login - Always check and auto-reload if mismatch
 * This ensures users always start with the latest version after login
 */
export const checkVersionOnLogin = async (): Promise<void> => {
  console.log('[Version Manager] Checking version on login...');
  await checkForUpdates({ autoReload: true });
};

/**
 * Check version on API request - Throttled to avoid excessive checks
 * This catches version updates while user is actively using the app
 */
export const checkVersionOnApiRequest = async (): Promise<void> => {
  const lastCheck = localStorage.getItem(LAST_CHECK_KEY);
  const now = Date.now();

  // Only check if last check was more than the throttle period ago
  if (!lastCheck || now - parseInt(lastCheck) > API_CHECK_THROTTLE) {
    localStorage.setItem(LAST_CHECK_KEY, now.toString());
    await checkForUpdates({ autoReload: true });
  }
};
